import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal from '../components/Shell/Modal';
import Button from '../components/Button';
import BetaAttributes from '../components/Attributes';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';

import useRequired from '../hooks/useRequired';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

import { addConsumable } from '../api/consumables';
import { getModuleFields } from '../api/modules';

const MODAL_NAME = 'addConsumable';

const QuickAddConsumable = () => {
	const navigate = useNavigate();
	const [form, setForm] = useState({});
	const [currentPage, setCurrentPage] = useState('general');
	const { open: options, setClose } = useModal(MODAL_NAME);

	const {
		value: moduleFields,
		execute: onGetModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFields, { immediate: false });

	const { execute: onAddConsumable, status: isAddingConsumable } = useAsync(
		addConsumable,
		{ immediate: false, showNotification: true }
	);

	useEffect(() => {
		if (options) {
			setForm({
				moduleId: options?.moduleId || null,
			});

			onGetModuleFields({ id: options?.moduleId, active: true });
		} else {
			setForm({});
			setCurrentPage('general');
		}
	}, [options]);

	const defaultModuleFields = moduleFields?.filter((field) => field?.isDefault);

	const customModuleFields = moduleFields?.filter((field) => !field?.isDefault);

	return (
		<Modal
			title={
				<div>
					<div className="my-3 mb-5">
						{options?.title || 'Add a Consumable'}
					</div>

					<nav aria-label="Progress">
						<ol
							role="list"
							className="space-y-4 md:flex md:space-x-4 md:space-y-0 mb-2"
						>
							{[
								{
									name: 'General',
									key: 'general',
									status: currentPage === 'general' ? 'current' : 'complete',
								},
								...(customModuleFields?.length
									? [
											{
												name: `Custom Attributes (${customModuleFields?.length})`,
												key: 'attributes',
												status:
													currentPage === 'attributes'
														? 'current'
														: currentPage === 'inventory'
														? 'complete'
														: undefined,
											},
									  ]
									: []),
							].map((step) => (
								<li key={step.name} className="md:flex-1">
									{step.status === 'complete' ? (
										<a
											href={step.href}
											className="cursor-pointer group flex flex-col border-l-4 rounded border-indigo-600 py-2 pl-4 hover:border-indigo-800 md:border-l-0 md:border-b-4 md:pb-0 md:pl-0 md:pb-4"
											onClick={() => setCurrentPage(step?.key)}
										>
											<span className="text-sm font-medium text-indigo-600 group-hover:text-indigo-800">
												{step.id}
											</span>
											<span className="text-xs font-medium">{step.name}</span>
										</a>
									) : step.status === 'current' ? (
										<a
											href={step.href}
											className="cursor-pointer flex flex-col border-l-4 border-indigo-600 py-2 pl-4 md:border-l-0 md:border-b-4 md:pb-0 md:pl-0 md:pb-4"
											aria-current="step"
											onClick={() => setCurrentPage(step?.key)}
										>
											<span className="text-sm font-medium text-indigo-600">
												{step.id}
											</span>
											<span className="text-xs font-medium">{step.name}</span>
										</a>
									) : (
										<a
											href={step.href}
											className="cursor-pointer roup flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-l-0 md:border-b-4 md:pb-0 md:pl-0 md:pb-4"
											onClick={() => setCurrentPage(step?.key)}
										>
											<span className="text-sm font-medium text-gray-500 group-hover:text-gray-700">
												{step.id}
											</span>
											<span className="text-xs font-medium">{step.name}</span>
										</a>
									)}
								</li>
							))}
						</ol>
					</nav>
				</div>
			}
			open={!!options}
			onClose={() => {
				if (typeof options?.onClose === 'function') {
					options?.onClose();
				}
				setClose(null);
			}}
		>
			{isLoadingModuleFields === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<>
					{currentPage === 'general' && (
						<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
							{defaultModuleFields?.map((field) => (
								<BetaAttributes
									field={{
										...field,
										...(field?.type === 'image' ? { maxImages: 1 } : {}),
									}}
									onChange={(index, value) => {
										if (value) {
											setForm({
												...form,
												[index]: { value, fieldId: field.id },
											});
										} else {
											let updatedForm = { ...form };
											delete updatedForm[index];
											setForm(updatedForm);
										}
									}}
									form={form}
								/>
							))}
						</div>
					)}

					{currentPage === 'attributes' && (
						<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
							{customModuleFields?.map((field) => (
								<BetaAttributes
									field={{
										...field,
										...(field?.type === 'image' ? { maxImages: 1 } : {}),
									}}
									onChange={(index, value) => {
										if (value) {
											setForm({
												...form,
												[index]: { value, fieldId: field.id },
											});
										} else {
											let updatedForm = { ...form };
											delete updatedForm[index];
											setForm(updatedForm);
										}
									}}
									form={form}
								/>
							))}
						</div>
					)}

					<div className="flex justify-between mt-6">
						<div>
							{customModuleFields?.length ? (
								<>
									<Button
										type="secondary"
										onClick={() => setCurrentPage('general')}
										disabled={currentPage === 'general'}
									>
										Back
									</Button>
									<Button
										type="secondary"
										onClick={() => setCurrentPage('attributes')}
										disabled={
											currentPage === 'attributes' ||
											!useRequired(
												form,
												moduleFields
													?.map((field) => field?.isRequired && field?.key)
													.filter(Boolean)
											)
										}
									>
										Next
									</Button>
								</>
							) : null}
						</div>
						<Button
							disabled={
								!useRequired(
									form,
									moduleFields
										?.map((field) => field?.isRequired && field?.key)
										.filter(Boolean)
								)
							}
							onClick={() =>
								onAddConsumable(
									configurePayload(form, moduleFields),
									(response) => {
										if (typeof options?.onSubmit === 'function') {
											options?.onSubmit({
												label: response.name,
												value: response.id,
												consumable: response,
											});
											setForm({});
										}
										setClose();
									}
								)
							}
						>
							{isAddingConsumable === 'pending' ? 'Adding' : 'Add'}
						</Button>
					</div>
				</>
			)}
		</Modal>
	);
};

const configurePayload = (form, fields) => {
	const returnForm = {
		customAttributes: [],
	};

	const updatedFormForDefault = {
		...Object.assign(
			{},
			...fields
				?.filter((field) => field?.isActive && field?.defaultValue)
				.map((field) => ({ [field?.key]: field?.defaultValue }))
		),
		...form,
	};

	Object.keys(updatedFormForDefault).map((key) => {
		const fieldId = updatedFormForDefault[key]?.fieldId;
		const field = fields.find((f) => f.id === fieldId);
		const fieldType = field?.type;
		const isDefault = field?.isDefault || field?.key === 'category';

		let value = field?.defaultValue || null;

		if (fieldType === 'vendor' || field?.key === 'category') {
			value = updatedFormForDefault[key]?.value?.value;
		} else {
			value = updatedFormForDefault[key]?.value;
		}

		if (isDefault) {
			returnForm[key] = value;
		} else {
			if (value) {
				returnForm.customAttributes.push({ fieldId, value, type: fieldType });
			}
		}
	});

	if (returnForm?.images?.length) {
		const { images, ...information } = returnForm;

		return {
			information: {
				...information,
				imageFieldId: updatedFormForDefault?.images?.fieldId,
			},
			files: images?.map(({ original }) => original),
			bucket: 'consumables',
		};
	}

	return returnForm;
};

export default QuickAddConsumable;
